import React, { useState, useEffect, useCallback, useRef } from "react";
import TextList from "./TextList";
import TextDetail from "./TextDetail";
import ConfirmDialog from "./ConfirmDialog";
import { baiduAnalytics } from "./utils/baiduAnalytics";

interface TextEditorProps {
  username: string;
  password: string;
  onLogout: () => void;
}

interface ListItem {
  index: number;
  content: string;
}

const TextEditor: React.FC<TextEditorProps> = ({ username, password, onLogout }) => {
  const [mode, setMode] = useState<"list" | "edit">("list");
  const [listItems, setListItems] = useState<ListItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedMaxIndex, setLoadedMaxIndex] = useState(-1);
  const [hasMore, setHasMore] = useState(true);
  const [message, setMessage] = useState("");
  const loadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const getKey = (index?: number) => {
    const keyStr = index !== undefined 
      ? `user=${username}&psw=${password}&index=${index}`
      : `user=${username}&psw=${password}`;
    return encodeURIComponent(keyStr);
  };

  const loadList = useCallback(async (startIndex: number = 0, count: number = 10) => {
    if (startIndex === 0) {
      setLoadingList(true);
      setListItems([]);
      setLoadedMaxIndex(-1);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }
    setMessage("");
    try {
      const promises = [];
      const endIndex = Math.min(startIndex + count, 100);
      for (let i = startIndex; i < endIndex; i++) {
        const key = getKey(i);
        promises.push(
          fetch(`https://textdb.online/${key}`)
            .then((response) => {
              if (response.ok) {
                return response.text().then((text) => {
                  return { index: i, content: text || "" };
                });
              }
              return { index: i, content: "" };
            })
            .catch(() => ({ index: i, content: "" }))
        );
      }
      const results = await Promise.all(promises);
      const newItems = results;
      const actualCount = newItems.length;
      const requestedCount = endIndex - startIndex;
      
      if (startIndex === 0) {
        newItems.sort((a, b) => a.index - b.index);
        setListItems(newItems);
        const maxIndex = newItems.length > 0 ? Math.max(...newItems.map(item => item.index)) : endIndex - 1;
        setLoadedMaxIndex(maxIndex);
        setHasMore(endIndex < 100 && actualCount >= requestedCount);
      } else {
        setListItems(prev => {
          const combined = [...prev, ...newItems];
          combined.sort((a, b) => a.index - b.index);
          return combined;
        });
        setLoadedMaxIndex(prev => Math.max(prev, endIndex - 1));
        setHasMore(endIndex < 100 && actualCount >= requestedCount);
      }
    } catch (error) {
      setMessage("加载列表失败，请检查网络连接");
    } finally {
      setLoadingList(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [username, password]);

  const loadMore = useCallback(async () => {
    const nextStartIndex = loadedMaxIndex + 1;
    baiduAnalytics.trackEvent('列表操作', '加载更多', `从${nextStartIndex}开始`);
    await loadList(nextStartIndex, 10);
  }, [loadList, loadedMaxIndex]);

  const loadContent = useCallback(async (index: number) => {
    setLoading(true);
    setMessage("");
    try {
      const key = getKey(index);
      const response = await fetch(`https://textdb.online/${key}`);
      if (response.ok) {
        const text = await response.text();
        setContent(text);
        setCurrentIndex(index);
        baiduAnalytics.trackEvent('文本操作', '查看文本', `条目${index}`);
      } else if (response.status === 404) {
        setContent("");
        setCurrentIndex(index);
        baiduAnalytics.trackEvent('文本操作', '创建新文本', `条目${index}`);
      } else {
        setMessage("加载失败，请重试");
      }
    } catch (error) {
      setMessage("加载失败，请检查网络连接");
    } finally {
      setLoading(false);
    }
  }, [username, password]);

  const saveContent = async () => {
    if (currentIndex === null) {
      setMessage("请先选择或创建条目");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const key = getKey(currentIndex);
      const response = await fetch(
        `https://textdb.online/update/?key=${encodeURIComponent(key)}&value=${encodeURIComponent(content)}`
      );
      if (response.ok) {
        setMessage("保存成功");
        setTimeout(() => setMessage(""), 2000);
        baiduAnalytics.trackEvent('文本操作', '保存文本', `条目${currentIndex}`, content.length);
        if (mode === "list") {
          await loadList();
        } else {
          const itemIndex = listItems.findIndex(item => item.index === currentIndex);
          if (itemIndex >= 0) {
            const updatedItems = [...listItems];
            updatedItems[itemIndex] = { index: currentIndex, content };
            setListItems(updatedItems);
          } else {
            const updatedItems = [...listItems, { index: currentIndex, content }];
            updatedItems.sort((a, b) => a.index - b.index);
            setListItems(updatedItems);
          }
        }
      } else {
        setMessage("保存失败，请重试");
      }
    } catch (error) {
      setMessage("保存失败，请检查网络连接");
    } finally {
      setSaving(false);
    }
  };

  const clearItem = async (index: number) => {
    setConfirmDialog({
      open: true,
      title: "确认清空",
      message: "确定要清空这个条目的内容吗？",
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: () => {} });
        setSaving(true);
        setMessage("");
        try {
          const key = getKey(index);
          const response = await fetch(
            `https://textdb.online/update/?key=${encodeURIComponent(key)}&value=`
          );
          if (response.ok) {
            setMessage("清空成功");
            setTimeout(() => setMessage(""), 2000);
            baiduAnalytics.trackEvent('文本操作', '清空文本', `条目${index}`);
            setListItems(prev => prev.map(item => item.index === index ? { ...item, content: "" } : item));
            if (currentIndex === index) {
              setContent("");
            }
          } else {
            setMessage("清空失败，请重试");
          }
        } catch (error) {
          setMessage("清空失败，请检查网络连接");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const updateUrlParams = (index: number | null) => {
    const params = new URLSearchParams(window.location.search);
    if (index !== null) {
      params.set("index", index.toString());
    } else {
      params.delete("index");
    }
    window.history.pushState({}, "", `?${params.toString()}`);
  };


  const handleEditItem = (index: number) => {
    baiduAnalytics.trackEvent('列表操作', '编辑条目', `条目${index}`);
    loadContent(index);
    setMode("edit");
    updateUrlParams(index);
  };

  useEffect(() => {
    const handlePopState = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlIndex = params.get("index");
      
      if (urlIndex !== null) {
        const index = parseInt(urlIndex, 10);
        if (!isNaN(index)) {
          setCurrentIndex(index);
          setMode("edit");
          await loadContent(index);
        }
      } else {
        setMode("list");
        setCurrentIndex(null);
        setContent("");
      }
    };

    const params = new URLSearchParams(window.location.search);
    const urlIndex = params.get("index");
    
    if (urlIndex !== null) {
      const index = parseInt(urlIndex, 10);
      if (!isNaN(index)) {
        setCurrentIndex(index);
        setMode("edit");
        loadContent(index);
      }
    }

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [loadContent]);

  useEffect(() => {
    if (mode === "list" && !hasLoadedRef.current && !loadingList && !loadingRef.current) {
      loadingRef.current = true;
      hasLoadedRef.current = true;
      loadList(0, 10);
    }
    return () => {
      if (mode !== "list") {
        loadingRef.current = false;
        hasLoadedRef.current = false;
      }
    };
  }, [mode, loadingList, loadList]);

  const buttonStyle = {
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#E0E0E0",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  };

  return (
    <div>
      {mode === "list" ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <h1
              style={{
                fontSize: "28px",
                fontWeight: "600",
                color: "#00FFD1",
                textShadow: "0 0 20px rgba(0, 255, 209, 0.5)",
              }}
            >
              文本列表
            </h1>
            <button
              onClick={onLogout}
              style={buttonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#00FFD1";
                e.currentTarget.style.boxShadow = "0 0 15px rgba(0, 255, 209, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              退出登录
            </button>
          </div>
          <TextList
            listItems={listItems}
            loadingList={loadingList}
            loadingMore={loadingMore}
            hasMore={hasMore}
            onEditItem={handleEditItem}
            onClearItem={clearItem}
            onLoadMore={loadMore}
          />
        </>
      ) : (
        <>
          <TextDetail
            currentIndex={currentIndex}
            content={content}
            loading={loading}
            saving={saving}
            message={message}
            onContentChange={setContent}
            onReload={() => currentIndex !== null && loadContent(currentIndex)}
            onSave={saveContent}
            onBackToList={() => {
              setMode("list");
              setCurrentIndex(null);
              setContent("");
              updateUrlParams(null);
              hasLoadedRef.current = false;
            }}
            onLogout={onLogout}
          />
        </>
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: () => {} })}
        confirmText="清空"
        cancelText="取消"
      />
    </div>
  );
};

export default TextEditor;
